import { Subject } from 'rxjs';

import { SelfV2 } from '../core/alcmeon-context/models/self-v2.model';
import { CapabilityIdEnum } from './capabilities-and-permissions';
import { SiteTypeEnum } from './enums';
import { SiteV2 } from './models';
import { Channel, Team } from './models/misc.model';

export interface TasksListSettings {
  selectedSiteIds: number[];
  selectedSiteGroupIds: number[];
  selectedChannelIds: number[];
  selectedTeamIds: number[];
  selectedSortOrderId: number;
}

export enum SortOrderId {
  PriorityMessages = 0,
  MostRecentMessages = 1,
  OldestMessages = 2,
  MostRecentConversations = 3,
  OldestConversations = 4,
}

const ALL_SORT_ORDER_IDS = [
  SortOrderId.PriorityMessages,
  SortOrderId.MostRecentConversations,
  SortOrderId.OldestConversations,
  SortOrderId.MostRecentMessages,
  SortOrderId.OldestMessages,
];

export const DEFAULT_SORT_ORDER_ID = SortOrderId.MostRecentMessages;

export class TasksListSettingsHelper {
  availableSites: SiteV2[];
  availableSiteIds: number[];
  availableChannels: Channel[];
  availableChannelIds: number[];
  availableTeams: Team[] = [];
  availableTeamIds: number[];
  availableSortOrderIds: number[];

  companyHasRoutingTag: boolean;

  settingsChanged$ = new Subject<void>();

  constructor(private settings: TasksListSettings) {}

  get selectedSiteIds() {
    return this.settings.selectedSiteIds;
  }

  set selectedSiteIds(value) {
    this.settings.selectedSiteIds = value;
  }

  get selectedChannelIds() {
    return this.settings.selectedChannelIds;
  }

  set selectedChannelIds(value) {
    this.settings.selectedChannelIds = value;
  }

  get selectedTeamIds() {
    return this.settings.selectedTeamIds;
  }

  set selectedTeamIds(value) {
    this.settings.selectedTeamIds = value;
  }

  get selectedSortOrderId() {
    return this.settings.selectedSortOrderId;
  }

  set selectedSortOrderId(value) {
    this.settings.selectedSortOrderId = value;
  }

  initFromSelf(self: SelfV2, sitesMap: Map<number, SiteV2>) {
    this.initAvailableSiteChannelsAndTeams(self, sitesMap);
    this.initAvailableSortOrders(self);

    // If settings loaded from storage don't match the available sites, channels or teams
    // (for example after some capabilities have been removed from the user),
    // we clear the settings so that stale values don't stick forever
    const settingsOK =
      this.selectedSiteIds.every(id => this.availableSiteIds.includes(id)) &&
      this.selectedChannelIds.every(id => this.availableChannelIds.includes(id)) &&
      this.selectedTeamIds.every(id => this.availableTeamIds.includes(id)) &&
      this.availableSortOrderIds.includes(this.selectedSortOrderId);
    if (!settingsOK) {
      this.settings.selectedSiteIds = [];
      this.settings.selectedChannelIds = [];
      this.settings.selectedTeamIds = [];
      this.settings.selectedSortOrderId = DEFAULT_SORT_ORDER_ID;
      this.settingsChanged$.next();
    }
  }

  // Checks how many settings are currently in effect (will be displayed as a badge over the settings button)
  numberOfNonDefaultSettings() {
    let result = 0;
    if (this.selectedSiteIds.length > 0 && this.selectedSiteIds.length < this.availableSites.length) {
      result++;
    }
    if (
      this.selectedChannelIds.length > 0 &&
      this.selectedChannelIds.length < this.availableChannels.filter(ch => !ch.isCategory).length
    ) {
      result++;
    }
    if (this.companyHasRoutingTag) {
      if (this.selectedTeamIds.length > 0 && this.selectedTeamIds.length < this.availableTeams.length) {
        result++;
      }
    }
    return result;
  }

  /**
   * Returns whether the given site matches the logged user capabilities as well as the sites (if any) selected in the settings,
   * the given task type matches the channels (if any) selected in the settings,
   * and the given team matches the logged user's teams as well as the teams (if any) selected in the settings.
   */
  matchesAvailableAndSelectedSitesChannelsAndTeams(siteId: number, taskType: number, routingTagId: number) {
    const siteMatches =
      this.selectedSiteIds.length > 0 ? this.selectedSiteIds.includes(siteId) : this.availableSiteIds.includes(siteId);
    const channelMatches = this.selectedChannelIds.length > 0 ? this.selectedChannelIds.includes(taskType) : true; // available channels is computed from available sites, no need to recheck
    let teamMatches = true;
    if (this.companyHasRoutingTag) {
      teamMatches =
        this.selectedTeamIds.length > 0
          ? this.selectedTeamIds.includes(routingTagId)
          : this.availableTeamIds.includes(routingTagId);
    }
    return siteMatches && channelMatches && teamMatches;
  }

  private initAvailableSiteChannelsAndTeams(self: SelfV2, sitesMap: Map<number, SiteV2>) {
    // Filter the sites for which the user has the 'answer' capability
    this.availableSiteIds = Object.keys(self.capabilities)
      .filter(siteId => self.capabilities[siteId]?.includes(CapabilityIdEnum.Answer))
      .map(i => +i);

    this.availableSites = this.availableSiteIds.map(siteId => sitesMap.get(+siteId)).filter(s => s);

    // Filter the channels for which there are corresponding sites
    this.availableChannels = allChannels.filter(channel =>
      this.availableSites.map(site => site.type_name).includes(channel.siteTypeName)
    );
    this.availableChannelIds = this.availableChannels.map(channel => channel.id);

    this.companyHasRoutingTag = self.routing_tags.length > 0;
    if (this.companyHasRoutingTag) {
      this.availableTeams = self.routing_tags.map(obj => ({ id: obj.id, name: obj.name }));
    }
    this.availableTeamIds = this.availableTeams.map(team => team.id);
  }

  private initAvailableSortOrders(self: SelfV2) {
    const availableSortOrderIdsFromBackend = self.companies[0].sort_types.map(t => t.id);

    // filter and order as specified in #4310
    this.availableSortOrderIds = ALL_SORT_ORDER_IDS.filter(id => availableSortOrderIdsFromBackend.includes(id));
  }
}

const INSTAGRAM_DIRECT_TYPE_ID = 37;

export const allChannels: Channel[] = [
  newCategory(SiteTypeEnum.Twitter),
  newChannel(12, SiteTypeEnum.Twitter, 'twitter/private'),
  newChannel(1, SiteTypeEnum.Twitter, 'twitter/mention'),

  newCategory(SiteTypeEnum.Facebook),
  newChannel(6, SiteTypeEnum.Facebook, 'facebook/private'),
  newChannel(7, SiteTypeEnum.Facebook, 'facebook/user_posts'),
  newChannel(8, SiteTypeEnum.Facebook, 'facebook/news_comments'),
  newChannel(20, SiteTypeEnum.Facebook, 'facebook/ad_comments'),
  newChannel(33, SiteTypeEnum.Facebook, 'facebook/ratings'),

  newCategory(SiteTypeEnum.Instagram2),
  newChannel(30, SiteTypeEnum.Instagram2, 'instagram-comments'),
  newChannel(INSTAGRAM_DIRECT_TYPE_ID, SiteTypeEnum.Instagram2, 'instagram-private'),

  newCategory(SiteTypeEnum.InMessage),
  newChannel(32, SiteTypeEnum.InMessage, 'webchat-msg'),

  newCategory(SiteTypeEnum.GoogleBusinessProfile),
  newChannel(34, SiteTypeEnum.GoogleBusinessProfile, 'gmb-reviews'),

  newCategory(SiteTypeEnum.GoogleBusinessMessages),
  newChannel(39, SiteTypeEnum.GoogleBusinessMessages, 'gbm-messages'),

  newCategory(SiteTypeEnum.WhatsApp),
  newChannel(35, SiteTypeEnum.WhatsApp, 'whatsapp-msg'),

  newCategory(SiteTypeEnum.AppleBusinessChat),
  newChannel(36, SiteTypeEnum.AppleBusinessChat, 'business-chat-msg'),

  newCategory(SiteTypeEnum.Trustpilot),
  newChannel(40, SiteTypeEnum.Trustpilot, 'trustpilot-service-review'),
  newChannel(41, SiteTypeEnum.Trustpilot, 'trustpilot-product-review'),

  newCategory(SiteTypeEnum.Line),
  newChannel(44, SiteTypeEnum.Line, 'line-msg'),

  newCategory(SiteTypeEnum.Kakaotalk),
  newChannel(45, SiteTypeEnum.Kakaotalk, 'kakaotalk-msg'),

  newCategory(SiteTypeEnum.Linkedin),
  newChannel(46, SiteTypeEnum.Linkedin, 'linkedin-comments'),
];

function newChannel(id, siteTypeName, taskTypeName) {
  return {
    id,
    siteTypeName,
    taskTypeName,
    isCategory: false,
  };
}

function newCategory(siteTypeName) {
  return {
    siteTypeName,
    isCategory: true,
  };
}
